import React from 'react';
import PropTypes from 'prop-types';
import ViPropTypes from '../propTypes';
import Icon from '@material-ui/core/Icon';
import { get, isFinite, defaultsDeep } from 'lodash';
import { I18n } from 'react-redux-i18n';
import GA from 'react-ga';
import DvrTypes from '../enum/DvrTypes';
import PlayerCameras from '../enum/PlayerCameras';
import Timeline from '../PlayerDashboard/Timeline';
import PlayerDashboard from '../PlayerDashboard';

const emptyFunction = () => { };

export default class MediaPlayer extends React.Component {

  static propTypes = {
    mediaLoaded: PropTypes.bool,
    showCameraIcons: PropTypes.bool,
    media: ViPropTypes.Media,
    clip: ViPropTypes.Clip,
    flowplayerKey: PropTypes.string.isRequired,
    currentCamera: PropTypes.string,
    onPlayerTimeChanged: PropTypes.func,
    onCameraChange: PropTypes.func,
    children: PropTypes.node,
    mobile: PropTypes.bool,
    customerSettings: ViPropTypes.CustomerSettings,
    userSettings: ViPropTypes.UserSettings
  }

  static defaultProps = {
    mediaLoaded: true,
    showCameraIcons: true,
    currentCamera: PlayerCameras.FORWARD_CAMERA,
    onPlayerTimeChanged: emptyFunction,
    onCameraChange: emptyFunction,
    children: null,
    mobile: false,
    customerSettings: {
      gpsSpeed: false
    },
    userSettings: {
      settings: {
        systemMeasure: 'imperial'
      }
    }
  }

  state = {
    playing: false,
    playerCurrentSecond: 0,
    showDashboardInFullscreen: false,
  }

  player = null

  constructor(props) {
    super(defaultsDeep(props, MediaPlayer.defaultProps));
  }

  componentDidMount() {
    this.initFlowplayer();
  }

  componentWillUnmount() {
    clearInterval(this.videoProgressInterval);
    if (this.player !== null) {
      this.player.shutdown();
    }
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const clip = get(this.props, 'clip', {});
    const newClip = get(newProps, 'clip', {});

    if (clip.id === newClip.id && clip.channel === newClip.channel) {
      return;
    }

    this.loadClip(newProps.clip);
  }

  initFlowplayer() {
    const flowplayer = window.flowplayer;
    const { clip, flowplayerKey } = this.props;

    this.player = flowplayer(this.playerElement, {
      key: flowplayerKey,
      adaptiveRatio: true,
      embed: false,
      fullscreen: true,
      splash: true,
      playlist: [
        [
          { mp4: clip.url }
        ]
      ]
    });

    // Fix flowplayer bug on IOS
    if (typeof window.MediaSource === 'undefined') {
      delete window.MediaSource;
    }

    // player events
    const that = this;
    this.handleGA = eventName => {
      GA.event({
        category: 'Player',
        action: eventName,
        label: 'Video Player'
      });
    };

    this.playerCurrentSecond = -1;
    this.videoProgressInterval = null;
    this.startVideoProgressChecking = () => {
      clearInterval(this.videoProgressInterval);
      this.videoProgressInterval = setInterval(this.checkVideoProgress, 250);
    };
    this.stopVideoProgressChecking = () => {
      clearInterval(this.videoProgressInterval);
    };
    this.startVideo = () => {
      this.startVideoProgressChecking();
      this.handleGA('Start');
    };
    this.finishVideo = () => {
      this.stopVideoProgressChecking();
      this.handleGA('Finish');
    };
    this.stopVideo = () => {
      this.stopVideoProgressChecking();
      this.handleGA('Stop');
    };
    this.fullscreen = on => () => {
      this.setState({ showDashboardInFullscreen: on });
      this.handleGA(`Fullscreen ${on ? 'on' : 'off'}`);
    };
    this.checkVideoProgress = (count, seek) => {
      let newValue;
      if (seek) {
        this.handleGA('Seek');
        newValue = Math.floor(seek);
      } else {
        newValue = Math.floor(that.player.video.time);
      }

      if (isFinite(newValue) && newValue !== that.playerCurrentSecond) {
        that.playerCurrentSecond = newValue;
        this.setState({ playerCurrentSecond: that.playerCurrentSecond });
        that.props.onPlayerTimeChanged(that.playerCurrentSecond);
      }

      // FIXING ISSUE about flowplayer.js #2126
      // # When video is playing and try touch(mobile) the video to pause, flowplayer trigger a
      // # stopPropagation to modify CSS Classes and this affect to pause the video in the first
      // # touch; Forcing the class is-mouseover, avoid this stopPropagation used in the
      // # flowplayer's lib;
      const classList = that.playerElement.classList;
      if (classList.contains('is-playing') && !classList.contains('is-mouseover')) {
        that.playerElement.classList.remove('is-mouseout');
        that.playerElement.classList.add('is-mouseover');
      }
    };

    this.player.bind('start', this.startVideo);
    this.player.bind('resume', this.startVideo);
    this.player.bind('stop', this.stopVideo);
    this.player.bind('pause', this.stopVideo);
    this.player.bind('finish', this.finishVideo);
    this.player.bind('fullscreen', this.fullscreen(true));
    this.player.bind('fullscreen-exit', this.fullscreen(false));
    this.player.bind('beforeseek', (e, api, seekTime) => {
      if (api.playing) {
        this.stopVideoProgressChecking();
      }
      this.checkVideoProgress(null, seekTime);
      if (api.playing) {
        this.startVideoProgressChecking();
      }
    });
    // end of player events
  }

  loadClip = clip => {
    this.setState({ playing: true });
    this.player.load(clip.url);
  }

  handleOnPlay = () => {
    this.loadClip(this.props.clip);
  }

  changeCamera = cameraView => event => {
    this.props.onCameraChange(cameraView, event);
  }

  renderThumb = () => {
    if (this.state.playing) {
      return null;
    }

    return (
      <div className="media__thumbcontainer">
        <div className="videothumb__playbtcont">
          <Icon className="fp-icon fp-playbtn media__playbt" onClick={this.handleOnPlay} id="playerPlayVideo" />
        </div>
        <img
          alt=""
          src={this.props.clip ? this.props.clip.thumbURL : ''}
          className="media__thumb"
          onClick={this.handleOnPlay}
        />
      </div>
    );
  }

  getMachineType() {
    return get(this.props, 'media.metadata.machineType', 0);
  }

  getDvrType() {
    return DvrTypes.forMachineType(this.getMachineType());
  }

  isTwoChannelDvr() {
    return this.getDvrType() === DvrTypes.TWO_CHANNEL_DVR;
  }

  isSplitView() {
    return this.isCurrentCamera(PlayerCameras.SPLIT_CAMERA);
  }

  isCurrentCamera(playerCamera) {
    const { currentCamera } = this.props;
    return playerCamera === currentCamera;
  }

  hasCamera(playerCamera) {
    const channel = this.getDvrType().getChannelNumber(playerCamera);

    if (!channel) {
      return false;
    }

    const { media } = this.props;
    const clips = get(media.captures, '[0].clips');

    if (!clips || clips.length <= 0) {
      return false;
    }

    return clips.some(clip => clip.channel === channel);
  }

  showCameraIcon(playerCamera) {
    if (this.isSplitView()) {
      return this.hasCamera(playerCamera);
    }

    return this.isCurrentCamera(playerCamera);
  }

  renderCameraIcons() {
    const { showCameraIcons } = this.props;
    if (!showCameraIcons) {
      return;
    }

    return (
      <React.Fragment>
        <button
          className={this.showCameraIcon(PlayerCameras.FORWARD_CAMERA) ? 'videothumb__playcircle media__camview-ff' : 'hideme'}
          onClick={this.changeCamera(PlayerCameras.FORWARD_CAMERA)}
        >
          <Icon className="videothumb__camview">arrow_upward</Icon>
          {I18n.t('page.playerPage.cameraViews.ff')}
        </button>

        <button
          className={this.showCameraIcon(PlayerCameras.DRIVER_CAMERA) ? `videothumb__playcircle media__camview-dr` : 'hideme'}
          onClick={this.changeCamera(PlayerCameras.DRIVER_CAMERA)}
        >
          <Icon className="videothumb__camview">arrow_back</Icon>
          {I18n.t('page.playerPage.cameraViews.dr')}
        </button>

        <button
          className={this.showCameraIcon(PlayerCameras.PASSENGER_CAMERA) ? 'videothumb__playcircle media__camview-pa' : 'hideme'}
          onClick={this.changeCamera(PlayerCameras.PASSENGER_CAMERA)}
        >
          <Icon className="videothumb__camview">arrow_forward</Icon>
          {I18n.t('page.playerPage.cameraViews.pa')}
        </button>

        <button
          className={this.showCameraIcon(PlayerCameras.REAR_CAMERA) ? 'videothumb__playcircle media__camview-re' : 'hideme'}
          onClick={this.changeCamera(PlayerCameras.REAR_CAMERA)}
        >
          <Icon className="videothumb__camview">arrow_downward</Icon>
          {I18n.t('page.playerPage.cameraViews.re')}
        </button>

        <button
          className={this.showCameraIcon(PlayerCameras.IN_CABIN_CAMERA) ? 'videothumb__playcircle media__camview-ca' : 'hideme'}
          onClick={this.changeCamera(PlayerCameras.IN_CABIN_CAMERA)}
        >
          <Icon className="videothumb__camview">arrow_downward</Icon>
          {I18n.t('page.playerPage.cameraViews.ca')}
        </button>
      </React.Fragment>
    );
  }

  renderDashboardFullscreen() {
    const classes = [];
    const {
      mobile,
      mediaLoaded,
      media,
      customerSettings,
      userSettings
    } = this.props;

    const {playerCurrentSecond} = this.state;

    return (
      <div
        id="desktopDashboardFullVideo"
        className={this.state.showDashboardInFullscreen ? 'dashboard__newDesktopDashboardFullscreen' : 'hideme'}>
        <Timeline
          mediaLoaded={mediaLoaded}
          media={media}
        />
        <PlayerDashboard
          mobile={mobile}
          mediaLoaded={mediaLoaded}
          currentSecond={playerCurrentSecond}
          media={media}
          customerSettings={customerSettings}
          userSettings={userSettings}
        />
      </div>
    )
  }

  render() {
    const classes = [];
    const classesFlowplayer = ['flowplayer', 'is-splash', 'is-mouseover', 'fixed-controls'];


    if (this.props.mediaLoaded) {
      classes.push('media__container');

      if (this.isSplitView() && this.isTwoChannelDvr()) {
        classes.push('media__two-channel-dvr');
      }
    } else {
      classes.push('hideme');
    }

    if (this.props.mobile) {
      classesFlowplayer.push('is-mobile-view');
    }

    return (
      <div className={classes.join(' ')}>
        {this.renderCameraIcons()}
        {this.renderThumb()}
        {this.props.children}
        <div
          ref={elem => {
            this.playerElement = elem;
          }}
          id="flowplayerElement"
          data-key={this.props.flowplayerKey}
          className={this.state.playing ? classesFlowplayer.join(' ') : 'hideme'}>
          {this.renderDashboardFullscreen()}
        </div>
      </div>
    );
  }
}
